import React, { useMemo } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import { OpenScoreboardData, ParticipantId } from '../../services/apiTypes'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import WinnerIcon from '@mui/icons-material/MilitaryTechRounded';
import EditIcon from '@mui/icons-material/CreateRounded';
import DeleteIcon from '@mui/icons-material/ClearRounded';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowUpRounded from '@mui/icons-material/KeyboardArrowUpRounded'
import Text from '../../components/Text'
import { UserInfo } from '../../components/UserInfo/UserInfo'
import { colors } from '../../styles/_var'
import { humanize, scoreboardParticipantToUser } from '../../utils/helpers'
import { ScoreboardParticipant } from '../../utils/types'
import { ParticipantCell, ScoreRow } from '../Dashboard/DateRequests/components/DateMemories/style'
import ActionButtons from '../Dashboard/DateRequests/components/DateMemories/ScoreboardView/ActionButtons'
import { maxRoundCount, orderedParticipantIds } from './helpers'
import { useUiStore } from '../../stores/uiStore'
import AddRoundModal from '../Dashboard/DateRequests/components/DateMemories/AddRoundModal'
import OpenScoreboardResult from '../../utils/openScoreboardResult'
import { useUpdateScoreboardResultMutation } from '../../services/api'
import { routes } from '../../utils/constants'

type ScoreboardResultProps = {
  result: OpenScoreboardData
  scoreboard_id: number
  hasWriteAccess: boolean
  page: number
  participants?: ScoreboardParticipant[]
  isExpanded: boolean
  onToggle: () => void
}

const useScoreboardResult = ({ scoreboard_id, page, result, participants=[], isExpanded, onToggle }: ScoreboardResultProps) => {
  const navigate = useNavigate()
  const scoreboardInstance = useMemo(() => {
    return new OpenScoreboardResult(participants || [], result)
  }, [participants, result]) 
  const scoresData = result.scores_data || {}
  const scoreKeys = Object.keys(scoresData)
  const roundCount = maxRoundCount(scoresData)
  const rowIds = scoreKeys.length > 0 ? orderedParticipantIds(scoresData, participants) : (participants ?? []).map((p) => String(p.id))
  const { showDialog } = useUiStore()
  const [updateScoreboardResult, { isLoading: isUpdatingScoreboardResult }] = useUpdateScoreboardResultMutation()

  const saveResultUpdate = async () => {
    const resultData = scoreboardInstance.getResultData()
    const winner = scoreboardInstance.deduceWinner()
    await updateScoreboardResult({
      page,
      scoreboard_id,
      result_id: result.id,
      label: resultData.label,
      scores_data: resultData.scores_data,
      winner: winner == null ? undefined : Number(winner),
    })
  }

  const editRound = (roundNumber: number) => async (payload: Record<number, string>) => {
    const transformedPayload = Object.entries(payload).reduce((prev, [participantId, score]) => {
      prev[participantId] = Number(score)
      return prev
    }, {} as Record<ParticipantId, number>)
    scoreboardInstance.updateRoundOfScores(roundNumber, transformedPayload)
    await saveResultUpdate()
  }

  const addNewRound = async (payload: Record<number, string>) => {
    const transformedPayload = Object.entries(payload).reduce((prev, [participantId, score]) => {
      prev[participantId] = Number(score)
      return prev
    }, {} as Record<ParticipantId, number>)
    scoreboardInstance.addNewRoundOfScores(transformedPayload)
    await saveResultUpdate()
  }

  const deleteRound = (roundNumber: number) => async () => {
    scoreboardInstance.deleteRoundOfScores(roundNumber)
    await saveResultUpdate()
  }

  const onAddOrEdit = (roundNumber?: number) => () => {
    const isEditing = roundNumber !== undefined
    const isSaving = isUpdatingScoreboardResult
    const getRoundScores = (roundNumber: number) => scoreboardInstance.getRoundScores(roundNumber)

    showDialog({
      dialogBody: (
        <AddRoundModal
          isEditing={isEditing}
          scoreboardId={result.id}
          isLoading={isSaving}
          onSubmit={isEditing ? editRound(roundNumber) : addNewRound}
          participants={participants}
          roundNumber={isEditing ? roundNumber + 1: roundCount + 1}
          initialData={isEditing ? getRoundScores(roundNumber) : undefined} 
          isTeam={false}
        />
      )
    })
  }

  const getParticipantByUserId = (userId: string | number) => {
    return participants?.find((p) => String(p.user_id) === String(userId))
  }

  const isWinner = (pid: string | number) => {
    const participant = result.winner?.user_id ? getParticipantByUserId(result.winner?.user_id) : null
    return String(participant?.id) === String(pid)
  }

  const goToScorecard = () => {
    navigate(
      generatePath(routes.SCOREBOARD_SCORECARD, {
        scoreboard_id: String(scoreboard_id),
        result_id: String(result.id),
      })
    )
  }

  return {
    scoresData,
    scoreKeys,
    roundCount,
    rowIds,
    onToggle,
    result,
    participants,
    isExpanded,
    onAddOrEdit,
    deleteRound,
    getParticipantByUserId,
    isWinner,
    goToScorecard,
  }
}

const ScoreboardResult = (props: ScoreboardResultProps) => {
  const { hasWriteAccess } = props
  const {
    scoresData,
    roundCount,
    rowIds,
    onToggle,
    result,
    participants,
    isExpanded,
    onAddOrEdit,
    deleteRound,
    isWinner,
    goToScorecard,
  } = useScoreboardResult(props)

  const renderRowCollapsibleContainer = () => {
    return (
      <Box
        px={1.5}
        py={1}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ cursor: 'pointer' }}
        onClick={onToggle}
      >
        <Text useDiv weight="semibold" variant="regular">
          {result.label || `Result ${result.id}`}
        </Text>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Text variant="small" useDiv color={colors.darkgrey}>
            {humanize(result.created_at, true)}
          </Text>
          <IconButton size="small">
            {isExpanded ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
          </IconButton>
        </Box>
      </Box>
    )
  }

  const renderTableHeader = () => {
    return (
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>
            <ActionButtons
              hasWinner={!!result.winner}
              onClickDownload={goToScorecard}
              showAddButton={hasWriteAccess}
              onClickAddIcon={onAddOrEdit()}
            />
          </TableCell>
          {Array.from({ length: roundCount }, (_, i) => (
            <TableCell key={i} align="center" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
              <Box
                flexDirection="column"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text weight="bold" variant="small">
                  Rd {i + 1}
                </Text>
                {hasWriteAccess && (
                  <Box ml={1}>
                    <IconButton
                      size="small"
                      color="success"
                      onClick={onAddOrEdit(i)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={deleteRound(i)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    )
  }

  const renderScoreResultRow = (participantId: string, index: number) => {
    const isLastRow = index === rowIds.length - 1
    const scores = scoresData[participantId] || []
    const participant = participants?.find((p) => String(p.id) === participantId)
    return (
      <ScoreRow key={participantId}>
        <ParticipantCell style={{alignItems: "center"}} sx={{ borderBottom: isLastRow ? 'none' : undefined }}>
          <UserInfo
            onlyShowName
            onlyFirstName
            useSmallAvatar
            titleTextVariant="regular"
            user={scoreboardParticipantToUser(participant)}
          />
          {isWinner(participantId) && (
            <WinnerIcon
              sx={{color: colors.lightPrimary}}
              fontSize="large"  />
          )}
        </ParticipantCell>
        {Array.from({ length: roundCount }, (_, roundIndex) => (
          <TableCell key={roundIndex} align="center" sx={{ borderBottom: isLastRow ? 'none' : undefined }}>
            <Text center>
              {scores[roundIndex] != null ? String(scores[roundIndex]) : '—'}
            </Text>
          </TableCell>
        ))}
      </ScoreRow>
    )
  }

  return (
    <Paper sx={{ mb: 1.5, borderRadius: 1 }}>
      {renderRowCollapsibleContainer()}
      <Collapse in={isExpanded}>
        <Divider />
        <Box px={1.5} py={1}>
          <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Table>
              {renderTableHeader()}
              <TableBody>
                {rowIds.map(renderScoreResultRow)}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Collapse>
    </Paper>
  )
}

export default ScoreboardResult
